import { Link, type LinkProps } from "react-router";
import { useTranslation } from "react-i18next";
import { Button, type ButtonProps } from "./Button";
import type { Language } from "~/lib/types";
import {
  SUPPORTED_LANGS,
  buildLocalizedPath,
  type RouteKey,
} from "~/config/route-config";

type AppLinkWithRoute = Omit<LinkProps, "to"> & {
  routeKey: RouteKey;
  params?: Record<string, string>;
  /** Optional in-page anchor appended to the resolved path, e.g. "#ritual-core-waitlist". */
  hash?: string;
  /** Optional query appended before the anchor, e.g. "?public=1" (T634's public access to the home). */
  search?: string;
  to?: never;
};

type AppLinkWithTo = Omit<LinkProps, "to"> & {
  to: string;
  routeKey?: never;
  params?: never;
};

export type AppLinkProps = (AppLinkWithRoute | AppLinkWithTo) & Pick<ButtonProps, "size" | "surface" | "trailingIcon"> & { variant?: Exclude<ButtonProps["variant"], "icon"> };

/**
 * Language-aware wrapper around Remix <Link>.
 *
 * Two usage modes:
 *  1. `routeKey` — resolves the fully localized path from route-config
 *     (e.g. <AppLink routeKey="contact"> → /es/contacto or /en/contact)
 *  2. `to` — fallback for paths not in route-config (hash links, external, etc.)
 *     Prepends /${lang} to absolute paths that aren't already prefixed.
 */
export function AppLink(props: AppLinkProps) {
  const { i18n } = useTranslation();
  const lang = (i18n.language ?? "es") as Language;
  const { variant, size, surface, trailingIcon, ...linkProps } = props;
  const render = (to: string, rest: Omit<LinkProps, "to">) => variant
    ? <Button href={to} variant={variant} size={size} surface={surface} trailingIcon={trailingIcon} {...rest} />
    : <Link to={to} {...rest} />;

  let resolved: string;

  if ("routeKey" in linkProps && linkProps.routeKey) {
    const { routeKey, params, hash, search, ...rest } = linkProps;
    resolved = buildLocalizedPath(routeKey, lang, params);
    return render(`${resolved}${search ?? ""}${hash ?? ""}`, rest);
  }

  const { to, ...rest } = linkProps as AppLinkWithTo;

  // `/es?public=1` and `/es#x` carry their language too (a home with a query or a hash).
  const alreadyPrefixed = SUPPORTED_LANGS.some(
    (l) => to === `/${l}` || to.startsWith(`/${l}/`) || to.startsWith(`/${l}?`) || to.startsWith(`/${l}#`),
  );

  resolved = to;
  if (to.startsWith("/") && !alreadyPrefixed) {
    resolved = to === "/" ? `/${lang}` : `/${lang}${to}`;
  }

  return render(resolved, rest);
}
